<template>
   <div class="text-left">
      <!-- Label + Input -->
      <div>
         <InputLabel
            v-if="props.label"
            :label="props.label"
            :labelHelper="props.labelHelper"
            :showIsOptional="props.showIsOptional"
            :id="id"
         />

         <input
            :id="`textbox-${id}-input`"
            ref="autoFocusInput"
            :value="modelValue"
            :autocomplete="autocomplete"
            :placeholder="placeholder"
            :data-cy="`textbox-${id}-input`"
            class="w-full box-border appearance-none rounded-lg border bg-grey-10 px-4 py-2 text-black"
            :class="[props.hasError && props.errorHelper ? 'border-error-red' : 'border-grey-20', props.inputClasses]"
            :type="props.type"
            :maxlength="maxLength"
            :disabled="disabled"
            :aria-describedby="inputDescribedBy"
            @input="onInput"
         />

         <span v-if="disabled" class="absolute right-0 top-1/2 p-2">
            <IconPadlock class="ml-1 size-3 shrink-0 fill-current text-current" />
         </span>
      </div>

      <!-- inline error -->
      <InvalidInputsList
         v-if="errorHelper && hasError"
         :id="`textbox-${id}-input-error`"
         :errorList="errorList"
         :data-cy="`textbox-${id}-error-helper`"
      />
   </div>
</template>

<script setup lang="ts">
interface ErrorHelper {
   code: string;
   text: string;
}

interface Props {
   id: string;
   modelValue?: string;
   label?: string;
   labelHelper?: string;
   autocomplete?: string;
   type?: string;
   maxLength?: number;
   hasError: boolean; // adds error style to input
   errorHelper?: ErrorHelper; // displays message under the input
   placeholder?: string;
   showIsOptional?: boolean;
   disabled?: boolean;
   autoFocus?: boolean;
   inputClasses?: string;
}

// declarations
//-----------------------------------------------
const emit = defineEmits(["update:modelValue", "change"]);

const props = withDefaults(defineProps<Props>(), {
   modelValue: "",
   autocomplete: "off",
   type: "text",
   hasError: false,
   showIsOptional: false,
   disabled: false,
   autoFocus: false,
   inputClasses: "",
});

// state
const autoFocusInput = ref(null);

// mounted
//-----------------------------------------------
onMounted(() => {
   if (props.autoFocus) {
      autoFocusInput.value.focus();
   }
});

// computed
//-----------------------------------------------
const errorList = computed(() => {
   if (!props.hasError) return [];
   return [
      {
         inputId: `textbox-${props.id}`,
         validator: props.errorHelper.code,
         message: props.errorHelper.text,
      },
   ];
});

const inputDescribedBy = computed(() => {
   // returns the aria-describedby for input, but only if error and/or helper is present
   // TODO: add always present wrappers to the label helper and error helper, so we can remove this
   let inputDescribedBy = "";

   if (props.label && props.labelHelper) {
      inputDescribedBy = `textbox-${props.id}-input-helper `;
   }
   if (props.hasError && props.errorHelper) {
      inputDescribedBy += `textbox-${props.id}-input-error`;
   }

   return inputDescribedBy.trim() || null;
});

// methods
//-----------------------------------------------
function onInput(event) {
   emit("update:modelValue", event.target.value);
   emit("change", event.target.value);
}
</script>

<style scoped></style>
